import { useCallback, useEffect, useState } from 'react';
import {
    CANCELLATION_STEPS,
    CancellationStep,
    FLOW_ANALYTICS_EVENTS,
    OFFER_TYPES,
    OfferType,
} from '../constants/cancellationFlow';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { stanNotify } from '@/lib/stanNotify';
import {
    useApplyFreeMonthOffer,
    useApplyTwoMonthDiscountOffer,
    useCancelPlatformSubscription,
} from '../queries/useCancellationOffers';
import { useUser } from '@/queries/useUser';

interface UseCancellationFlowProps {
    platformSubscriptionId: string | null;
    hasTakenFreeMonth?: boolean;
    shouldShowOffer: boolean; // Qualification check from parent
    onComplete: () => void;
}

export const useCancellationFlow = ({
    platformSubscriptionId,
    hasTakenFreeMonth = false,
    shouldShowOffer,
    onComplete,
}: UseCancellationFlowProps) => {
    // Get user data for subscription end date (will be refetched after cancellation)
    const { user } = useUser();

    // Compute subscription end date from user data (reactive - updates when user is refetched)
    const subscriptionEndDate = user?.subscriptions?.platform?.valid_until
        ? new Date(user.subscriptions.platform.valid_until * 1000).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
          })
        : '';

    // Mutation hooks
    const { mutateAsync: applyFreeMonth, isPending: isApplyingFreeMonth } = useApplyFreeMonthOffer();
    const { mutateAsync: applyTwoMonthDiscount, isPending: isApplyingDiscount } = useApplyTwoMonthDiscountOffer();
    const { mutateAsync: cancelSubscription, isPending: isCancelling } = useCancelPlatformSubscription();

    // Combined loading state for offer acceptance
    const isOfferLoading = isApplyingFreeMonth || isApplyingDiscount;
    const isSurveyLoading = isCancelling;

    // useState only uses initial value once, so step won't reset when shouldShowOffer changes after offer acceptance
    const [currentStep, setCurrentStep] = useState<CancellationStep>(
        shouldShowOffer ? CANCELLATION_STEPS.OFFER : CANCELLATION_STEPS.SURVEY
    );
    const offerType: OfferType = hasTakenFreeMonth ? OFFER_TYPES.TWO_MONTHS_DISCOUNT : OFFER_TYPES.FREE_MONTH;

    // Track offer viewed when showing offer
    useEffect(() => {
        if (currentStep === CANCELLATION_STEPS.OFFER && shouldShowOffer) {
            const offerName = offerType === OFFER_TYPES.FREE_MONTH ? 'one free month' : '50% off - 2 Months';
            const stepName =
                offerType === OFFER_TYPES.FREE_MONTH ? 'offer one free month' : 'offer two months discount';

            stanAnalytics(FLOW_ANALYTICS_EVENTS.OFFER_VIEWED, {
                props: {
                    step_name: stepName,
                    offer_name: offerName,
                },
            });
        }
    }, [currentStep, offerType, shouldShowOffer]);

    // Handle survey completion or skip - proceeds to cancel subscription
    const handleSurveyComplete = useCallback(async () => {
        // Cancel the subscription
        if (!platformSubscriptionId) {
            console.error('No platform subscription ID - cannot cancel');
            stanNotify({
                type: 'error',
                title: 'Error',
                text: 'Unable to find subscription to cancel. Please contact support.',
            });
            onComplete();
            return;
        }

        await cancelSubscription(platformSubscriptionId);

        stanNotify({
            type: 'success',
            title: 'Success!',
            text: 'Your subscription has been canceled.',
        });

        // Track confirmation
        stanAnalytics(FLOW_ANALYTICS_EVENTS.CONFIRMATION_VIEWED, {
            props: {
                step_name: 'cancel subscription confirmation',
            },
        });

        // Show goodbye modal (with emojis)
        setCurrentStep(CANCELLATION_STEPS.GOODBYE);
    }, [platformSubscriptionId, cancelSubscription, onComplete]);

    // Handle offer acceptance
    const handleOfferAccept = useCallback(async () => {
        const offerName = offerType === OFFER_TYPES.FREE_MONTH ? 'one free month' : '50% off - 2 Months';
        const stepName = offerType === OFFER_TYPES.FREE_MONTH ? 'offer one free month' : 'offer two months discount';

        try {
            // Call appropriate API
            if (offerType === OFFER_TYPES.FREE_MONTH) {
                await applyFreeMonth({});
            } else {
                await applyTwoMonthDiscount({});
            }

            // Track acceptance
            stanAnalytics(FLOW_ANALYTICS_EVENTS.OFFER_ACCEPTED, {
                props: {
                    step_name: stepName,
                    offer_name: offerName,
                },
            });

            // Show offer accepted modal
            setCurrentStep(CANCELLATION_STEPS.OFFER_ACCEPTED);

            // Track confirmation viewed
            const confirmationEvent =
                offerType === OFFER_TYPES.FREE_MONTH
                    ? FLOW_ANALYTICS_EVENTS.FREE_MONTH_CONFIRMATION
                    : FLOW_ANALYTICS_EVENTS.TWO_MONTHS_CONFIRMATION;

            stanAnalytics(confirmationEvent, {
                props: {
                    step_name:
                        stepName === 'offer one free month'
                            ? 'offer one free month accepted'
                            : 'offer two months discount accepted',
                    offer_name: offerName,
                },
            });

            stanNotify({
                type: 'success',
                title: 'Success!',
                text:
                    offerType === OFFER_TYPES.FREE_MONTH
                        ? 'Free month has been applied to your account!'
                        : '50% discount has been applied for the next 2 months!',
            });
        } catch (error) {
            console.error('❌ Error accepting offer:', error);
            stanNotify({
                type: 'error',
                title: 'Error',
                text: 'Failed to apply offer. Please try again.',
            });
        }
    }, [offerType, applyFreeMonth, applyTwoMonthDiscount]);

    // Handle offer decline - proceed to survey
    const handleOfferDecline = useCallback(() => {
        const offerName = offerType === OFFER_TYPES.FREE_MONTH ? 'one free month' : '50% off - 2 Months';
        const stepName = offerType === OFFER_TYPES.FREE_MONTH ? 'offer one free month' : 'offer two months discount';

        // Track offer ignored
        stanAnalytics(FLOW_ANALYTICS_EVENTS.OFFER_IGNORED, {
            props: {
                step_name: stepName,
                offer_name: offerName,
            },
        });

        // Go to survey
        setCurrentStep(CANCELLATION_STEPS.SURVEY);
    }, [offerType]);

    // Handle offer accepted modal close
    const handleOfferAcceptedClose = useCallback(() => {
        // Close the entire flow
        onComplete();
    }, [onComplete]);

    // Handle emoji click in goodbye modal
    const handleGoodbyeEmojiClick = useCallback((satisfaction: string) => {
        // Track flow survey start
        stanAnalytics(FLOW_ANALYTICS_EVENTS.FLOW_SURVEY, {
            props: {
                step_name: 'cancellation flow survey',
            },
        });

        // Track rating
        stanAnalytics(FLOW_ANALYTICS_EVENTS.FLOW_SURVEY_RATING, {
            props: {
                step_name: 'cancellation flow survey',
                value: satisfaction,
            },
        });
    }, []);

    // Handle goodbye close - end the flow
    const handleGoodbyeClose = useCallback(() => {
        onComplete();
    }, [onComplete]);

    return {
        currentStep,
        offerType,
        isOfferLoading,
        isSurveyLoading,
        subscriptionEndDate,
        handleSurveyComplete,
        handleOfferAccept,
        handleOfferDecline,
        handleOfferAcceptedClose,
        handleGoodbyeEmojiClick,
        handleGoodbyeClose,
    };
};
